import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ROUTES } from '@/lib/constants';
import { queryKeys } from '@/lib/query/query-keys';
import { showNotification } from '@/services/notification';
import { useAudienceStore } from '@/stores/audience-store';
import { useAudienceBuilderUiStore } from '@/stores/audience-builder-ui-store';
import { getDraft, submitDraft } from '../services/audience-draft-api';
import type { AudienceDraft, DraftPatch } from '../types/draft';
import { draftFromStore } from '../lib/draft-store-sync';
import type { useAutosave } from './use-autosave';
import type { LocalDraftMirror } from './use-local-draft-mirror';

interface UseDraftActionsArgs {
  autosave: ReturnType<typeof useAutosave>;
  mirror: LocalDraftMirror;
  getPendingLocal: () => DraftPatch | null;
  setPendingLocal: (p: DraftPatch | null) => void;
  setShowRecoveryBanner: (v: boolean) => void;
  hydrate: (src: AudienceDraft | DraftPatch) => void;
  setLastSavedPatch: (p: DraftPatch | null) => void;
}

export interface DraftActions {
  applyLocalRecovery: () => void;
  discardLocalRecovery: () => void;
  reloadLatestFromServer: () => Promise<void>;
  keepMyChanges: () => Promise<void>;
  proceedToReview: () => Promise<void>;
  submitNow: () => Promise<void>;
  /**
   * Saves every pending edit before a submit (B14). Throws, with a message the caller
   * shows, when the save fails or is blocked, so the submit never goes out.
   */
  flushBeforeSubmit: () => Promise<void>;
}

const SUBMIT_BLOCKED_MESSAGE = 'Please resolve save issues before submitting.';

const SAVE_BLOCKER_KINDS = new Set(['conflict', 'error_validation', 'error_terminal']);
const POST_FLUSH_BLOCKER_KINDS = new Set([
  'conflict',
  'error_validation',
  'error_terminal',
  'error_transient',
]);

export function useDraftActions({
  autosave,
  mirror,
  getPendingLocal,
  setPendingLocal,
  setShowRecoveryBanner,
  hydrate,
  setLastSavedPatch,
}: UseDraftActionsArgs): DraftActions {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const applyLocalRecovery = useCallback(() => {
    const p = getPendingLocal();
    if (!p) return;
    hydrate(p);
    setShowRecoveryBanner(false);
    // Hydration clears hasUserEdited, so the subscriber won't mark dirty.
    // Explicitly mark dirty — local recovery IS a meaningful change that
    // must be persisted to the server.
    useAudienceBuilderUiStore.getState().markUserEdited();
    autosave.markDirty(p);
  }, [autosave, hydrate, getPendingLocal, setShowRecoveryBanner]);

  const discardLocalRecovery = useCallback(() => {
    mirror.clear();
    setPendingLocal(null);
    setShowRecoveryBanner(false);
  }, [mirror, setPendingLocal, setShowRecoveryBanner]);

  const reloadLatestFromServer = useCallback(async () => {
    const { draftId } = useAudienceStore.getState();
    if (!draftId) return;
    const fresh = await getDraft(draftId);
    // NPJB-839: server copy first. `hydrate` clears the user-edited flag, so the identity
    // change below no longer re-queues this tab's stale store.
    hydrate(fresh);
    useAudienceStore.getState().setDraftIdentity({
      draftId: fresh.id,
      lastSeenUpdatedAt: fresh.updatedAt,
      status: fresh.status,
      following: fresh.following,
    });
    mirror.clear();
    // A recovery banner still up would re-apply this tab's stale local copy.
    setPendingLocal(null);
    setShowRecoveryBanner(false);
    // The server copy is now what "saved" means: an edit back to this tab's old state
    // must still count as a change.
    setLastSavedPatch(draftFromStore(useAudienceStore.getState()));
    // The rejected edit is still queued (and a save may be in flight). Drop both, or they
    // go out with the fresh token and overwrite the other tab's change.
    autosave.discardPending();
    autosave.acknowledgeConflictResolved();
  }, [autosave, hydrate, mirror, setLastSavedPatch, setPendingLocal, setShowRecoveryBanner]);

  const keepMyChanges = useCallback(async () => {
    const { draftId } = useAudienceStore.getState();
    if (!draftId) return;
    const fresh = await getDraft(draftId);
    useAudienceStore.getState().setLastSeenUpdatedAt(fresh.updatedAt);
    autosave.acknowledgeConflictResolved();
    void autosave.flushNow();
  }, [autosave]);

  /** True once every pending edit is on the server. */
  const flushPending = useCallback(async (): Promise<boolean> => {
    if (SAVE_BLOCKER_KINDS.has(autosave.getCurrentStateKind())) return false;
    await autosave.flushNow();
    if (POST_FLUSH_BLOCKER_KINDS.has(autosave.getCurrentStateKind())) return false;
    // A failed save that is waiting on a retry leaves its patch queued.
    return !autosave.hasPendingChanges();
  }, [autosave]);

  const flushAndContinue = useCallback(
    async (blockerMessage: string): Promise<boolean> => {
      const ok = await flushPending();
      if (!ok) showNotification({ message: blockerMessage, type: 'error' });
      return ok;
    },
    [flushPending],
  );

  const flushBeforeSubmit = useCallback(async () => {
    const ok = await flushPending();
    if (!ok) throw new Error(SUBMIT_BLOCKED_MESSAGE);
  }, [flushPending]);

  const proceedToReview = useCallback(async () => {
    const ok = await flushAndContinue('Please resolve save issues before continuing.');
    if (!ok) return;
    navigate(ROUTES.audienceReview);
  }, [flushAndContinue, navigate]);

  const submitNow = useCallback(async () => {
    const ok = await flushAndContinue(SUBMIT_BLOCKED_MESSAGE);
    if (!ok) return;
    const { draftId, lastSeenUpdatedAt, target } = useAudienceStore.getState();
    if (!draftId || !lastSeenUpdatedAt) return;
    const submitted = await submitDraft(
      draftId,
      lastSeenUpdatedAt,
      target.name ? { name: target.name } : undefined,
    );
    await queryClient.invalidateQueries({ queryKey: queryKeys.audienceRequest.all });
    mirror.clear();
    navigate(ROUTES.audienceRequestConfirmation(submitted.id));
  }, [flushAndContinue, mirror, navigate, queryClient]);

  return {
    applyLocalRecovery,
    discardLocalRecovery,
    reloadLatestFromServer,
    keepMyChanges,
    proceedToReview,
    submitNow,
    flushBeforeSubmit,
  };
}
